import { useBooleanState, useDisclosure } from "@gettactic/helpers";
import React from "react";
import { FormWrapper } from "../../../form/FormWrapper";
import { Drawer, DrawerProps } from "../../Drawer";
import { ConfirmModal } from "../ConfirmModal";
import { Modal } from "../Modal";
import { ModalCancelButton } from "../buttons/ModalCancelButton";
import { ModalConfirmButton } from "../buttons/ModalConfirmButton";
import { CrudModalProps } from "./CrudModal.types";
import { TrashIcon } from "lucide-react";

export const CrudModal: React.FC<CrudModalProps> = (props) => {
  const {
    message,
    onOpenChange,
    submitProps,
    cancelProps,
    isOpen,
    submitLabel: _submitLabel,
    cancelLabel = "Cancel",
    footerProps,
    onSubmit,
    children,
    isEditing = false,
    unmountContent = true,
    form,
    isDirty,
    confirmWhenDirty = true,
    onFormReset,
    defaultValues,
    asDrawer = false,
    onDelete,
    deleteLabel = "Delete",
    deleteProps,
    autoFocusFirstField,
    contentClassName,
    size,
    ...rest
  } = props;

  const isLoading = useBooleanState(false);
  const confirmCloseDisclosure = useDisclosure();
  const confirmDeleteDisclosure = useDisclosure();
  const successViewDisclosure = useDisclosure();
  const contentRef = React.useRef<HTMLDivElement>(null);
  const submitLabel = _submitLabel || (isEditing ? "Save" : "Add");

  // Auto-focus first input when modal opens (default: true when not editing)
  const shouldAutoFocus = autoFocusFirstField ?? !isEditing;
  React.useEffect(() => {
    if (isOpen && shouldAutoFocus) {
      // Small delay to ensure the modal content is rendered
      const timeoutId = setTimeout(() => {
        const firstInput = contentRef.current?.querySelector<HTMLElement>(
          'input:not([type="hidden"]), textarea, select, [tabindex]:not([tabindex="-1"])'
        );
        firstInput?.focus();
      }, 50);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [isOpen, shouldAutoFocus]);

  const handleOnSubmit = async () => {
    if (onSubmit) {
      isLoading.true();
      try {
        await onSubmit();
      } finally {
        isLoading.false();
      }
    }
  };

  const handleDeleteClick = () => {
    confirmDeleteDisclosure.onOpen();
  };

  const handleDeleteConfirm = async () => {
    if (onDelete) {
      try {
        await onDelete();
      } finally {
        confirmDeleteDisclosure.onClose();
      }
    }
  };

  const handleFormReset = () => {
    if (onFormReset) onFormReset();
    else form?.reset(defaultValues);
    successViewDisclosure.close();
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      onOpenChange(true);
      return;
    }

    if (confirmWhenDirty && (isDirty || form?.formState?.isDirty)) {
      confirmCloseDisclosure.onOpen();
      return;
    }

    if (isLoading.state) return;
    onOpenChange(false);
  };

  const handleClose = () => {
    handleOpenChange?.(false);
  };

  /*const handleSubmit = form.handleSubmit(async (data) => {
    isLoading.true();
    try {
      const result = await onSubmit?.(data);
      if (result === false) {
        isLoading.false();
        return;
      }
      if (showSuccessView && getSuccessView) {
        setSuccessView(
          getSuccessView?.(result, {
            onOpenChange,
            reset: handleDrawerReset
          })
        );
        return;
      }
      onSuccess?.(result);
      if (successMessage) {
        toastSuccess(successMessage);
      }
      onOpenChange(false);
    } catch (error) {
      isLoading.false();

      logError(error);
      const errorMessage = getApiErrorMessage(error);
      toastError(errorMessage);
      onError?.(error);
    } finally {
      isLoading.false();
    }
  });*/

  const sanitizedDefaultValues = JSON.stringify(defaultValues);
  React.useEffect(() => {
    handleFormReset();
  }, [sanitizedDefaultValues]);

  React.useEffect(() => {
    if (isOpen) {
      handleFormReset();
    }
  }, [isOpen]);

  const buttons = (
    <>
      {onDelete && (
        <ModalConfirmButton
          {...deleteProps}
          onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
            event.preventDefault();
            event.stopPropagation();
            handleDeleteClick();
          }}
          iconLeft={<TrashIcon />}
          variant={"destructive"}
          disabled={isLoading.state}
        >
          {deleteLabel}
        </ModalConfirmButton>
      )}
      <div className="flex-1" />
      <ModalCancelButton
        {...cancelProps}
        onClick={handleClose}
        disabled={isLoading.state}
      >
        {cancelLabel}
      </ModalCancelButton>
      <ModalConfirmButton
        {...submitProps}
        onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) => {
          event.preventDefault();
          event.stopPropagation();
          handleOnSubmit();
        }}
        isLoading={isLoading.state}
      >
        {submitLabel}
      </ModalConfirmButton>
    </>
  );

  const content = (
    <div ref={contentRef} className="h-full">
      <FormWrapper
        onSubmit={handleOnSubmit}
        isLoading={isLoading.state}
        className="h-full"
      >
        {message}
        {children}
        <input
          type={"hidden"}
          value={JSON.stringify(form?.formState?.isDirty)}
        />
      </FormWrapper>
    </div>
  );

  return (
    <>
      {asDrawer ? (
        <Drawer
          footer={buttons}
          isOpen={isOpen}
          {...rest}
          size={size as DrawerProps["size"]}
          contentClassName={contentClassName}
          unmountContent={unmountContent}
          closeOnOverlayClick={
            isLoading.state ? false : rest.closeOnOverlayClick
          }
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              handleClose();
              return;
            }

            onOpenChange?.(true);
          }}
        >
          {content}
        </Drawer>
      ) : (
        <Modal
          isOpen={isOpen}
          {...rest}
          size={size}
          unmountContent={unmountContent}
          closeOnOverlayClick={
            isLoading.state ? false : rest.closeOnOverlayClick
          }
          onOpenChange={handleOpenChange}
          onClose={() => null}
        >
          {content}
          <Modal.Footer {...footerProps}>{buttons}</Modal.Footer>
        </Modal>
      )}
      <ConfirmModal
        onOpenChange={confirmCloseDisclosure.onOpenChange}
        isOpen={confirmCloseDisclosure.isOpen}
        title={"Are you sure you want to close?"}
        message={
          "You have unsaved changes. All changes will be lost. Are you sure you want to close?"
        }
        onConfirm={() => {
          onOpenChange(false);
          confirmCloseDisclosure.onClose();
        }}
        confirmProps={{ severity: "critical" }}
        confirmLabel={"Close without save"}
      />
      <ConfirmModal
        onOpenChange={confirmDeleteDisclosure.onOpenChange}
        isOpen={confirmDeleteDisclosure.isOpen}
        title={"Are you sure you want to delete?"}
        message={
          "This action cannot be undone. Are you sure you want to delete?"
        }
        onConfirm={handleDeleteConfirm}
        confirmProps={{ severity: "critical" }}
        confirmLabel={"Delete"}
      />
    </>
  );
};
